/**
 * Date Range Picker Component
 * Intuitive calendar-based date picker for selecting start and end dates
 */
import React, { useState, useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';
import { getDatesInRange } from '../utils/dateHelpers';

const DateRangePicker = ({ 
  startDate, 
  endDate, 
  onStartDateChange, 
  onEndDateChange, 
  minDate = null,
  maxDate = null,
  blackoutDays = [],
  disabledDates = [],
  className = ''
}) => {
  // Calculate initial month based on minDate (which considers minAdvanceNoticeDays)
  const getInitialMonth = () => {
    const today = dayjs().startOf('day');
    let effectiveMinDate = today;
    
    if (minDate) {
      const minDateObj = dayjs(minDate).startOf('day');
      effectiveMinDate = minDateObj.isAfter(today) ? minDateObj : today;
    }
    
    return {
      month: effectiveMinDate.month(),
      year: effectiveMinDate.year()
    };
  };
  
  const initialDate = getInitialMonth();
  const [currentMonth, setCurrentMonth] = useState(initialDate.month);
  const [currentYear, setCurrentYear] = useState(initialDate.year);
  const [selectingStart, setSelectingStart] = useState(true);
  
  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];
  
  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  
  // Normalize blackout days once - ensure they're in YYYY-MM-DD format
  const normalizedBlackoutDays = useMemo(() => {
    if (!Array.isArray(blackoutDays) || blackoutDays.length === 0) {
      console.log('DateRangePicker: blackoutDays prop is empty or not an array', { blackoutDays });
      return [];
    }
    
    const normalized = blackoutDays
      .filter(date => date != null) // Filter out null/undefined
      .map(date => {
        // Convert to string and trim whitespace
        const dateStr = String(date).trim();
        
        // If date is already in YYYY-MM-DD format, return as is
        if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
          return dateStr;
        }
        // Try to parse and format the date
        const parsed = dayjs(dateStr);
        if (parsed.isValid()) {
          return parsed.format('YYYY-MM-DD');
        }
        console.warn('DateRangePicker: Invalid blackout date format', { original: date, dateStr });
        return null;
      })
      .filter(date => date !== null);
    
    console.log('DateRangePicker: Normalized blackout days', {
      original: blackoutDays,
      normalized,
      count: normalized.length
    });
    
    return normalized;
  }, [blackoutDays]);
  
  // Get all dates in the current month
  const calendarDays = useMemo(() => {
    const firstDay = dayjs(`${currentYear}-${currentMonth + 1}-01`);
    const daysInMonth = firstDay.daysInMonth();
    const startOfMonth = firstDay.startOf('month');
    const endOfMonth = firstDay.endOf('month');
    const startDayOfWeek = startOfMonth.day();
    
    const days = [];
    
    // Add previous month's trailing days
    for (let i = startDayOfWeek - 1; i >= 0; i--) {
      const date = startOfMonth.subtract(i + 1, 'day');
      days.push({
        date,
        isCurrentMonth: false,
        isToday: date.isSame(dayjs(), 'day'),
        isSelected: false,
        isInRange: false,
        isDisabled: false,
        isBlackout: false,
      });
    }
    
    // Add current month's days
    for (let i = 1; i <= daysInMonth; i++) {
      const date = dayjs(`${currentYear}-${currentMonth + 1}-${String(i).padStart(2, '0')}`);
      const dateStr = date.format('YYYY-MM-DD');
      const isSelected = 
        (startDate && date.isSame(dayjs(startDate), 'day')) ||
        (endDate && date.isSame(dayjs(endDate), 'day'));
      
      let isInRange = false;
      if (startDate && endDate) {
        const range = getDatesInRange(startDate, endDate);
        isInRange = range.includes(dateStr);
      } else if (startDate && selectingStart === false) {
        // When selecting end date, highlight range from start
        const range = getDatesInRange(startDate, date.format('YYYY-MM-DD'));
        isInRange = range.includes(dateStr);
      }
      
      // Always disable dates before today
      const today = dayjs().startOf('day');
      const isBeforeToday = date.isBefore(today, 'day');
      
      // Calculate effective minDate (use the later of today or minDate)
      let effectiveMinDate = today;
      if (minDate) {
        const minDateObj = dayjs(minDate).startOf('day');
        effectiveMinDate = minDateObj.isAfter(today) ? minDateObj : today;
      }
      
      const isDisabled = 
        isBeforeToday ||
        date.isBefore(effectiveMinDate, 'day') ||
        (maxDate && date.isAfter(dayjs(maxDate), 'day')) ||
        disabledDates.includes(dateStr);
      
      // Check if this date is a blackout day
      // Use robust comparison to handle any type/whitespace issues
      const isBlackout = normalizedBlackoutDays.some(bd => {
        const bdStr = String(bd).trim();
        const dateStrTrimmed = String(dateStr).trim();
        return bdStr === dateStrTrimmed;
      });
      
      // Debug for December 25, 2025
      if (dateStr === '2025-12-25') {
        console.log('December 25, 2025 - Blackout check:', {
          dateStr,
          isBlackout,
          normalizedBlackoutDays,
          isInArray: normalizedBlackoutDays.includes('2025-12-25'),
          arrayLength: normalizedBlackoutDays.length,
          firstFew: normalizedBlackoutDays.slice(0, 5)
        });
      }
      
      days.push({
        date,
        dateStr,
        isCurrentMonth: true,
        isToday: date.isSame(dayjs(), 'day'),
        isSelected,
        isInRange,
        isDisabled,
        isBlackout,
      });
      
      // Debug for December 25, 2025 during calendar generation
      if (dateStr === '2025-12-25') {
        console.log('Calendar generation - December 25, 2025:', {
          dateStr,
          isBlackout,
          normalizedBlackoutDays,
          checkResult: normalizedBlackoutDays.includes(dateStr),
          normalizedArray: normalizedBlackoutDays
        });
      }
    }
    
    // Add next month's leading days to fill the grid
    const remainingDays = 42 - days.length; // 6 rows × 7 days
    for (let i = 1; i <= remainingDays; i++) {
      const date = endOfMonth.add(i, 'day');
      days.push({
        date,
        isCurrentMonth: false,
        isToday: date.isSame(dayjs(), 'day'),
        isSelected: false,
        isInRange: false,
        isDisabled: false,
        isBlackout: false,
      });
    }
    
    return days;
  }, [currentMonth, currentYear, startDate, endDate, selectingStart, minDate, maxDate, disabledDates, normalizedBlackoutDays]);
  
  // Debug: Log blackout days when they change
  useEffect(() => {
    if (normalizedBlackoutDays && normalizedBlackoutDays.length > 0) {
      console.log('DateRangePicker: Normalized blackout days', normalizedBlackoutDays);
      console.log('DateRangePicker: Original blackout days prop', blackoutDays);
    } else {
      console.log('DateRangePicker: No blackout days found', { 
        blackoutDaysProp: blackoutDays,
        normalizedCount: normalizedBlackoutDays?.length || 0
      });
    }
  }, [normalizedBlackoutDays, blackoutDays]);
  
  const handleDateClick = (day) => {
    if (day.isDisabled || day.isBlackout || !day.isCurrentMonth) return;
    
    const dateStr = day.dateStr || day.date.format('YYYY-MM-DD');
    
    if (selectingStart) {
      // Selecting start date
      onStartDateChange(dateStr);
      setSelectingStart(false);
      
      // If end date is before new start date, clear it
      if (endDate && dayjs(dateStr).isAfter(dayjs(endDate), 'day')) {
        onEndDateChange('');
      }
    } else {
      // Selecting end date
      if (!startDate || dayjs(dateStr).isAfter(dayjs(startDate), 'day') || dayjs(dateStr).isSame(dayjs(startDate), 'day')) {
        onEndDateChange(dateStr);
        setSelectingStart(true);
      } else {
        // If clicked date is before start date, make it the new start date
        onStartDateChange(dateStr);
        onEndDateChange('');
        setSelectingStart(false);
      }
    }
  };
  
  const navigateMonth = (direction) => {
    if (direction === 'prev') {
      if (currentMonth === 0) {
        setCurrentMonth(11);
        setCurrentYear(currentYear - 1);
      } else {
        setCurrentMonth(currentMonth - 1);
      }
    } else {
      if (currentMonth === 11) {
        setCurrentMonth(0);
        setCurrentYear(currentYear + 1);
      } else {
        setCurrentMonth(currentMonth + 1);
      }
    }
  };
  
  const goToToday = () => {
    const initial = getInitialMonth();
    setCurrentMonth(initial.month);
    setCurrentYear(initial.year);
  };
  
  return (
    <div className={`glass p-4 ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <button
          onClick={() => navigateMonth('prev')}
          className="p-1.5 hover:bg-gray-100 rounded transition-colors"
        >
          <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <div className="flex items-center space-x-3">
          <h3 className="text-base font-bold text-gray-800">
            {monthNames[currentMonth]} {currentYear}
          </h3>
          <button
            onClick={goToToday}
            className="px-3 py-1 text-xs text-primary-600 hover:bg-primary-50 rounded font-medium"
          >
            Today
          </button>
        </div>
        <button
          onClick={() => navigateMonth('next')}
          className="p-1.5 hover:bg-gray-100 rounded transition-colors"
        >
          <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>
      </div>
      
      {/* Date Selection Mode Indicator */}
      <div className="mb-3 flex items-center justify-center space-x-3">
        <div className={`px-3 py-1.5 rounded text-sm font-medium ${
          selectingStart 
            ? 'bg-primary-500 text-white' 
            : 'bg-gray-100 text-gray-600'
        }`}>
          {startDate ? dayjs(startDate).format('MMM D') : 'Start'}
        </div>
        <span className="text-gray-400 text-sm">→</span>
        <div className={`px-3 py-1.5 rounded text-sm font-medium ${
          !selectingStart 
            ? 'bg-primary-500 text-white' 
            : 'bg-gray-100 text-gray-600'
        }`}>
          {endDate ? dayjs(endDate).format('MMM D') : 'End'}
        </div>
      </div>
      
      {/* Day Names */}
      <div className="grid grid-cols-7 gap-1 mb-2">
        {dayNames.map(day => (
          <div key={day} className="text-center text-xs font-semibold text-gray-600 py-1">
            {day}
          </div>
        ))}
      </div>
      
      {/* Calendar Grid */}
      <div className="grid grid-cols-7 gap-1">
        {calendarDays.map((day, index) => {
          // Disable dates before today
          const isBeforeToday = day.date.isBefore(dayjs().startOf('day'), 'day');
          const isDisabledDate = day.isDisabled || isBeforeToday;
          
          // Blackout days should be clearly marked in red
          const isBlackoutDay = day.isBlackout;
          
          // Debug specific date (December 25, 2025)
          if (day.dateStr === '2025-12-25') {
            console.log('December 25, 2025 check:', {
              dateStr: day.dateStr,
              isBlackout: day.isBlackout,
              normalizedBlackoutDays,
              isInBlackoutArray: normalizedBlackoutDays.includes(day.dateStr)
            });
          }
          
          const baseClasses = `
            aspect-square flex items-center justify-center rounded text-sm font-medium
            transition-all duration-200
            ${!day.isCurrentMonth ? 'text-gray-300' : ''}
            ${day.isToday && day.isCurrentMonth && !isBlackoutDay ? 'ring-2 ring-primary-400' : ''}
            ${day.isSelected ? 'bg-primary-500 text-white shadow-md z-10' : ''}
            ${day.isInRange && !day.isSelected && !isBlackoutDay ? 'bg-primary-100 text-primary-700' : ''}
            ${isDisabledDate && !isBlackoutDay ? 'opacity-30 cursor-not-allowed bg-gray-100' : ''}
            ${isBlackoutDay ? 'bg-red-200 text-red-800 border-2 border-red-400 font-bold cursor-not-allowed' : ''}
            ${!day.isSelected && !day.isInRange && !isDisabledDate && !isBlackoutDay && day.isCurrentMonth
              ? 'hover:bg-gray-100 cursor-pointer' : ''}
            ${!day.isCurrentMonth ? 'opacity-40' : ''}
          `;
          
          return (
            <button
              key={index}
              onClick={() => handleDateClick(day)}
              disabled={isDisabledDate || isBlackoutDay || !day.isCurrentMonth}
              className={baseClasses}
              title={
                isBlackoutDay
                  ? `Blackout day - ${day.date.format('MMMM D, YYYY')} - not available` 
                  : isBeforeToday
                  ? 'Past dates are not available'
                  : day.isDisabled 
                  ? 'Date not available' 
                  : day.date.format('MMMM D, YYYY')
              }
            >
              {day.date.date()}
            </button>
          );
        })}
      </div>
      
      {/* Legend */}
      <div className="mt-3 pt-3 border-t border-gray-200 flex flex-wrap items-center justify-center gap-3 text-xs">
        <div className="flex items-center space-x-2">
          <div className="w-4 h-4 bg-primary-500 rounded"></div>
          <span>Selected</span>
        </div>
        <div className="flex items-center space-x-2">
          <div className="w-4 h-4 bg-primary-100 rounded"></div>
          <span>Range</span>
        </div>
        <div className="flex items-center space-x-2">
          <div className="w-4 h-4 bg-red-200 border border-red-400 rounded"></div>
          <span className="font-semibold text-red-800">Blackout</span>
        </div>
        <div className="flex items-center space-x-2">
          <div className="w-4 h-4 bg-gray-100 rounded"></div>
          <span>Disabled</span>
        </div>
      </div>
    </div>
  );
};

DateRangePicker.propTypes = {
  startDate: PropTypes.string,
  endDate: PropTypes.string,
  onStartDateChange: PropTypes.func.isRequired,
  onEndDateChange: PropTypes.func.isRequired,
  minDate: PropTypes.string,
  maxDate: PropTypes.string,
  blackoutDays: PropTypes.arrayOf(PropTypes.string),
  disabledDates: PropTypes.arrayOf(PropTypes.string),
  className: PropTypes.string,
};

export default DateRangePicker;

